import { findConfirmedPivots, sessionVwap } from './analysis';
import { aggregateConfirmedMinutes } from './history';
import type { Bar } from '../types';

export type LevelRole = 'support' | 'resistance' | 'reference';
export interface MarketLevel { name: string; price: number; source: string; observedAt: number; role: LevelRole }
export interface LevelContext extends MarketLevel { currentRole: LevelRole; situation: string; distance: number }
const eastern = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
function easternParts(time: number) {
  const parts = Object.fromEntries(eastern.formatToParts(time * 1000).filter(part => part.type !== 'literal').map(part => [part.type, Number(part.value)]));
  return { day: `${parts.year}-${parts.month}-${parts.day}`, minute: parts.hour * 60 + parts.minute };
}
function nextTradingDay(day: string) {
  const date = new Date(`${day}T00:00:00Z`);
  do { date.setUTCDate(date.getUTCDate() + 1); } while (date.getUTCDay() === 0 || date.getUTCDay() === 6);
  return date.toISOString().slice(0, 10);
}
function extreme(bars: Bar[], kind: 'high' | 'low') {
  return kind === 'high' ? bars.reduce((a, b) => b.high > a.high ? b : a) : bars.reduce((a, b) => b.low < a.low ? b : a);
}

export function marketLevels(oneMinute: Bar[], currentPrice: number, maximum = 12): MarketLevel[] {
  if (!oneMinute.length) return [];
  const ordered = [...oneMinute].sort((a, b) => a.time - b.time);
  const byDay = new Map<string, { rth: Bar[]; overnight: Bar[]; opening: Bar[] }>();
  for (const bar of ordered) {
    const { day, minute } = easternParts(bar.time);
    if (minute > 960 && minute < 1080) continue; // post-RTH interval, before the next CME evening session
    const sessionDay = minute >= 1080 ? nextTradingDay(day) : day;
    const entry = byDay.get(sessionDay) ?? { rth: [], overnight: [], opening: [] };
    if (minute > 570 && minute <= 960) {
      entry.rth.push(bar);
      if (minute <= 585) entry.opening.push(bar);
    } else entry.overnight.push(bar);
    byDay.set(sessionDay, entry);
  }
  const latestParts = easternParts(ordered.at(-1)!.time);
  const today = latestParts.minute >= 1080 ? nextTradingDay(latestParts.day) : latestParts.day;
  const prior = [...byDay.keys()].filter(day => day < today && byDay.get(day)!.rth.length).at(-1);
  const levels: MarketLevel[] = [];
  const addPair = (name: string, source: string, bars: Bar[]) => {
    if (!bars.length) return;
    const high = extreme(bars, 'high'), low = extreme(bars, 'low');
    levels.push({ name: `${name} high`, source, price: high.high, observedAt: high.time, role: 'resistance' });
    levels.push({ name: `${name} low`, source, price: low.low, observedAt: low.time, role: 'support' });
  };
  const current = byDay.get(today)!;
  addPair('Current RTH', 'Session', current.rth);
  addPair('Overnight', 'Session', current.overnight);
  if (current.opening.length >= 15) addPair('Opening range', '15m opening', current.opening);
  if (prior) {
    const previous = byDay.get(prior)!;
    addPair('Previous RTH', 'Prior session', previous.rth);
    if (previous.rth.length) levels.push({ name: 'Previous RTH close', source: 'Prior session',
      price: previous.rth.at(-1)!.close, observedAt: previous.rth.at(-1)!.time, role: 'reference' });
    addPair('Previous overnight', 'Prior session', previous.overnight);
    if (previous.opening.length >= 15) addPair('Previous opening range', 'Prior session', previous.opening);
  }
  const vwapBars = [...current.overnight, ...current.rth].sort((a, b) => a.time - b.time);
  if (vwapBars.length && vwapBars.every(bar => bar.volume >= 0)) {
    const value = sessionVwap(vwapBars).at(-1)?.vwap;
    if (value != null && Number.isFinite(value)) levels.push({ name: 'ETH session VWAP', source: '1m bar approximation', price: value, observedAt: vwapBars.at(-1)!.time, role: 'reference' });
  }
  for (const [tf, name] of [['15m', '15m pivot'], ['1h', '1h pivot']] as const) {
    const bars = aggregateConfirmedMinutes(ordered, tf);
    const pivots = findConfirmedPivots(bars).filter(pivot => pivot.label);
    for (const kind of ['high', 'low'] as const) {
      const pivot = pivots.filter(item => item.kind === kind).at(-1);
      if (pivot) levels.push({ name: `${name} ${kind === 'high' ? 'resistance' : 'support'}`, source: name,
        price: pivot.price, observedAt: pivot.confirmedAt, role: kind === 'high' ? 'resistance' : 'support' });
    }
  }
  return levels.sort((a, b) => Math.abs(a.price - currentPrice) - Math.abs(b.price - currentPrice)).slice(0, maximum);
}

export function describeLevel(level: MarketLevel, bars: Bar[]): LevelContext {
  const latest = bars.at(-1);
  const distance = latest ? level.price - latest.close : Number.NaN;
  if (!latest) return { ...level, currentRole: level.role, situation: 'Awaiting price', distance };
  const ranges = bars.slice(-20).map(bar => bar.high - bar.low);
  const atr = ranges.reduce((sum, range) => sum + range, 0) / Math.max(1, ranges.length);
  const buffer = Math.max(1, atr * 0.25);
  const near = Math.abs(distance) <= Math.max(2, atr * 0.5);
  if (level.role === 'reference') return { ...level, currentRole: 'reference',
    situation: near ? `At ${level.name}` : latest.close > level.price ? `Above ${level.name}` : `Below ${level.name}`, distance };
  let role: LevelRole = level.role;
  let beyondCount = 0, forceful = false;
  for (let index = 0; index < bars.length; index++) {
    const bar = bars[index];
    if (bar.time <= level.observedAt) continue;
    const prior = bars.slice(Math.max(0, index - 20), index);
    const priorRange = prior.reduce((sum, item) => sum + item.high - item.low, 0) / Math.max(1, prior.length);
    const barBuffer = Math.max(1, priorRange * 0.25);
    const through = role === 'resistance' ? bar.close > level.price + barBuffer : bar.close < level.price - barBuffer;
    if (through) {
      beyondCount++;
      forceful ||= bar.high - bar.low >= priorRange * 1.25;
      if (beyondCount >= 2 && forceful) { role = role === 'resistance' ? 'support' : 'resistance'; beyondCount = 0; forceful = false; }
    } else { beyondCount = 0; forceful = false; }
  }
  const throughNow = role === 'resistance' ? latest.close > level.price + buffer : latest.close < level.price - buffer;
  let situation: string;
  if (throughNow) situation = role === 'resistance' ? 'Possible breakthrough' : 'Possible breakdown';
  else if (near && role !== level.role) situation = role === 'support' ? 'Possible support retest' : 'Possible resistance retest';
  else if (near) situation = role === 'resistance' ? 'Resistance retest' : 'Support retest';
  else if (role !== level.role) situation = role === 'support' ? 'Possible support after break' : 'Possible resistance after break';
  else situation = role === 'resistance' ? 'Resistance overhead' : 'Support below';
  return { ...level, currentRole: role, situation, distance };
}
